/**
 * Store - État global de l'application
 * - Patterns (séquences)
 * - Paramètres des synthétiseurs
 * - Settings (BPM)
 * Avec sauvegarde automatique dans LocalStorage
 */

import {
    saveToLocalStorage,
    loadFromLocalStorage,
    exportToJSON,
    importFromJSON,
    getDefaultPatterns,
    getDefaultPatternBanks,
    getDefaultSynthParams,
    getDefaultSettings,
    getDefaultMixerState,
    PATTERN_BANKS
} from './Persistence.js';

export class Store {
    constructor() {
        // Notes disponibles (3 octaves: C1 à C4)
        this.availableNotes = [
            'C1', 'C#1', 'D1', 'D#1', 'E1', 'F1', 'F#1', 'G1', 'G#1', 'A1', 'A#1', 'B1',
            'C2', 'C#2', 'D2', 'D#2', 'E2', 'F2', 'F#2', 'G2', 'G#2', 'A2', 'A#2', 'B2',
            'C3', 'C#3', 'D3', 'D#3', 'E3', 'F3', 'F#3', 'G3', 'G#3', 'A3', 'A#3', 'B3',
            'C4'
        ];

        // Format prêt pour les selects (calculé une seule fois)
        this.noteOptions = this.availableNotes.map(note => ({
            value: note,
            label: note
        }));

        // Charger depuis LocalStorage (ou défaut)
        const saved = loadFromLocalStorage();
        this.patternBanks = saved.patternBanks;
        this.synthParams = saved.synths;
        this.settings = saved.settings;
        this.mixerState = saved.mixer || getDefaultMixerState();

        // Pattern courant (A-H)
        this.currentPatternId = this.settings.currentPattern || 'A';

        // `patterns` pointe vers la banque courante (rétro-compatibilité)
        this.patterns = this.patternBanks[this.currentPatternId];

        // Clipboard pour copy/paste
        this.clipboard = null;

        // Liste des instruments pour les scenes
        this.instrumentIds = [
            'tb303_1', 'tb303_2', 'tr808', 'tr909',
            'minimoog', 'ms20', 'jupiter8', 'juno106',
            'sh101', 'arp2600', 'theremin', 'minimod'
        ];

        // Scene Mode (initialize BEFORE song mode because song uses scenes)
        this.scenes = saved.scenes || this.getDefaultScenes();
        this.currentSceneId = this.settings.currentSceneId || null;
        this.sceneMode = this.settings.sceneMode || false;
        this.sceneChangeListeners = [];

        // Song Mode (now uses scenes)
        this.song = this._migrateLegacySong(saved.song) || this.getDefaultSong();
        this.songMode = this.settings.songMode || false;
        this.songPosition = { stepIndex: 0, bar: 0 }; // Position courante dans le song
        this.songChangeListeners = [];

        // Panel states (collapsed/expanded)
        this.panelStates = this.settings.panelStates || {};

        // Listeners pour les changements
        this.listeners = [];
        this.patternChangeListeners = [];

        // Sauvegarde automatique activée
        this.autoSave = true;
    }

    // ============================================
    // PATTERN BANKS (A-H)
    // ============================================

    /**
     * Retourne l'ID du pattern courant (A-H)
     */
    getCurrentPatternId() {
        return this.currentPatternId;
    }

    /**
     * Retourne la liste des IDs de patterns disponibles
     */
    getPatternBankIds() {
        return PATTERN_BANKS;
    }

    /**
     * Change le pattern courant
     * @param {string} patternId - 'A' à 'H'
     */
    switchPattern(patternId) {
        if (!PATTERN_BANKS.includes(patternId)) return;

        this.currentPatternId = patternId;
        this.patterns = this.patternBanks[patternId];
        this.settings.currentPattern = patternId;

        this.notifyPatternChange(patternId);
        this.notifyListeners();
        this.save();
    }

    /**
     * Copie le pattern courant dans le clipboard
     */
    copyPattern() {
        // Deep copy du pattern courant
        this.clipboard = JSON.parse(JSON.stringify(this.patterns));
    }

    /**
     * Colle le clipboard dans le pattern courant
     */
    pastePattern() {
        if (!this.clipboard) return;

        // Deep copy du clipboard vers le pattern courant
        const pasted = JSON.parse(JSON.stringify(this.clipboard));
        Object.assign(this.patterns, pasted);
        this.patternBanks[this.currentPatternId] = this.patterns;

        this.notifyListeners();
        this.save();
    }

    /**
     * Efface le pattern courant (remet aux valeurs par défaut)
     */
    clearCurrentPattern() {
        const defaults = getDefaultPatterns();
        Object.assign(this.patterns, defaults);
        this.patternBanks[this.currentPatternId] = this.patterns;

        this.notifyListeners();
        this.save();
    }

    /**
     * Vérifie si le clipboard contient un pattern
     */
    hasClipboard() {
        return this.clipboard !== null;
    }

    /**
     * Enregistre un callback pour les changements de pattern (A-H)
     */
    onPatternChange(callback) {
        this.patternChangeListeners.push(callback);
        return () => {
            this.patternChangeListeners = this.patternChangeListeners.filter(l => l !== callback);
        };
    }

    notifyPatternChange(patternId) {
        this.patternChangeListeners.forEach(cb => cb(patternId));
    }

    // ============================================
    // SONG MODE (now uses Scenes)
    // ============================================

    /**
     * Migre un ancien format de song (pattern) vers le nouveau format (sceneId)
     * @param {Array} song - Song à migrer
     * @returns {Array|null} - Song migré ou null
     */
    _migrateLegacySong(song) {
        if (!song || !Array.isArray(song)) return null;

        // Vérifier si migration nécessaire (ancien format avec 'pattern')
        const needsMigration = song.some(step => step.pattern && !step.sceneId);

        if (!needsMigration) return song;

        // Migrer chaque step
        const sceneIds = Object.keys(this.scenes);
        const defaultSceneId = sceneIds[0] || 'scene_1';

        return song.map(step => {
            if (step.sceneId) return step; // Déjà nouveau format

            // Ancien format - convertir pattern en sceneId
            return {
                sceneId: defaultSceneId,
                bars: step.bars || 4
            };
        });
    }

    /**
     * Retourne un song par défaut
     * Le song est maintenant une séquence de scenes (et non de patterns globaux)
     */
    getDefaultSong() {
        // Utilise les scenes existantes
        const sceneIds = Object.keys(this.scenes || {});
        const firstScene = sceneIds[0] || 'scene_1';
        return [
            { sceneId: firstScene, bars: 4 }
        ];
    }

    /**
     * Retourne le song actuel
     */
    getSong() {
        return this.song;
    }

    /**
     * Définit le song complet
     */
    setSong(song) {
        this.song = song;
        this.notifySongChange();
        this.save();
    }

    /**
     * Ajoute un step au song
     * @param {string} sceneId - ID de la scene à jouer
     * @param {number} bars - Nombre de bars
     */
    addSongStep(sceneId = null, bars = 4) {
        // Utiliser la première scene si non spécifié
        if (!sceneId) {
            const sceneIds = Object.keys(this.scenes);
            sceneId = sceneIds[0] || 'scene_1';
        }
        this.song.push({ sceneId, bars });
        this.notifySongChange();
        this.save();
    }

    /**
     * Supprime un step du song
     */
    removeSongStep(index) {
        if (this.song.length > 1) {
            this.song.splice(index, 1);
            // Ajuster la position si nécessaire
            if (this.songPosition.stepIndex >= this.song.length) {
                this.songPosition.stepIndex = 0;
                this.songPosition.bar = 0;
            }
            this.notifySongChange();
            this.save();
        }
    }

    /**
     * Met à jour un step du song
     * @param {number} index - Index du step
     * @param {string} sceneId - ID de la scene
     * @param {number} bars - Nombre de bars
     */
    updateSongStep(index, sceneId, bars) {
        if (this.song[index]) {
            this.song[index] = { sceneId, bars };
            this.notifySongChange();
            this.save();
        }
    }

    /**
     * Déplace un step vers le haut
     */
    moveSongStepUp(index) {
        if (index > 0) {
            [this.song[index - 1], this.song[index]] = [this.song[index], this.song[index - 1]];
            this.notifySongChange();
            this.save();
        }
    }

    /**
     * Déplace un step vers le bas
     */
    moveSongStepDown(index) {
        if (index < this.song.length - 1) {
            [this.song[index], this.song[index + 1]] = [this.song[index + 1], this.song[index]];
            this.notifySongChange();
            this.save();
        }
    }

    /**
     * Active/désactive le mode song
     */
    setSongMode(enabled) {
        this.songMode = enabled;
        this.settings.songMode = enabled;

        if (enabled) {
            // Song mode implique scene mode
            this.sceneMode = true;
            this.settings.sceneMode = true;

            // Reset position
            this.songPosition = { stepIndex: 0, bar: 0 };

            // Lancer la première scene du song
            if (this.song.length > 0) {
                const firstStep = this.song[0];
                let firstSceneId = null;

                if (firstStep.sceneId && this.scenes[firstStep.sceneId]) {
                    firstSceneId = firstStep.sceneId;
                } else {
                    const sceneIds = Object.keys(this.scenes);
                    if (sceneIds.length > 0) {
                        firstSceneId = sceneIds[0];
                    }
                }

                if (firstSceneId) {
                    this.currentSceneId = firstSceneId;
                    this.settings.currentSceneId = firstSceneId;
                }
            }
        }

        // Toujours notifier les deux types de listeners
        this.notifySceneChange();
        this.notifySongChange();
        this.save();
    }

    /**
     * Retourne si le mode song est actif
     */
    isSongMode() {
        return this.songMode;
    }

    /**
     * Retourne la position courante dans le song
     */
    getSongPosition() {
        return this.songPosition;
    }

    /**
     * Appelé par le Sequencer à chaque nouveau bar
     * Retourne true si on a changé de scene
     */
    advanceSongBar() {
        if (!this.songMode || this.song.length === 0) return false;

        this.songPosition.bar++;
        const currentStep = this.song[this.songPosition.stepIndex];

        // Vérifier si on doit passer au prochain step du song
        if (this.songPosition.bar >= currentStep.bars) {
            this.songPosition.bar = 0;
            this.songPosition.stepIndex++;

            // Loop si on arrive à la fin
            if (this.songPosition.stepIndex >= this.song.length) {
                this.songPosition.stepIndex = 0;
            }

            // Changer de scene directement (sans passer par launchScene)
            const nextStep = this.song[this.songPosition.stepIndex];
            let nextSceneId = null;

            // Support both new (sceneId) and legacy (pattern) format
            if (nextStep.sceneId && this.scenes[nextStep.sceneId]) {
                nextSceneId = nextStep.sceneId;
            } else {
                // Fallback: utiliser la première scene disponible
                const sceneIds = Object.keys(this.scenes);
                if (sceneIds.length > 0) {
                    nextSceneId = sceneIds[0];
                }
            }

            // Appliquer le changement de scene
            if (nextSceneId && nextSceneId !== this.currentSceneId) {
                this.currentSceneId = nextSceneId;
                this.settings.currentSceneId = nextSceneId;

                // IMPORTANT: Notifier les DEUX types de listeners
                this.notifySceneChange();
            }

            this.notifySongChange();
            this.save();
            return true;
        }

        this.notifySongChange();
        return false;
    }

    /**
     * Reset la position du song (au stop)
     */
    resetSongPosition() {
        this.songPosition = { stepIndex: 0, bar: 0 };

        if (this.songMode && this.song.length > 0) {
            const firstStep = this.song[0];
            let firstSceneId = null;

            if (firstStep.sceneId && this.scenes[firstStep.sceneId]) {
                firstSceneId = firstStep.sceneId;
            } else {
                const sceneIds = Object.keys(this.scenes);
                if (sceneIds.length > 0) {
                    firstSceneId = sceneIds[0];
                }
            }

            if (firstSceneId && firstSceneId !== this.currentSceneId) {
                this.currentSceneId = firstSceneId;
                this.settings.currentSceneId = firstSceneId;
                this.notifySceneChange();
            }
        }

        this.notifySongChange();
    }

    /**
     * Enregistre un callback pour les changements de song
     */
    onSongChange(callback) {
        this.songChangeListeners.push(callback);
        return () => {
            this.songChangeListeners = this.songChangeListeners.filter(l => l !== callback);
        };
    }

    notifySongChange() {
        this.songChangeListeners.forEach(cb => cb({
            song: this.song,
            songMode: this.songMode,
            position: this.songPosition
        }));
    }

    // ============================================
    // SCENES
    // ============================================

    /**
     * Retourne les scenes par défaut
     */
    getDefaultScenes() {
        return {
            'scene_1': this._createDefaultScene('Scene 1')
        };
    }

    /**
     * Crée une scene par défaut (tous les instruments sur pattern A)
     */
    _createDefaultScene(name) {
        const instruments = {};
        this.instrumentIds.forEach(id => {
            instruments[id] = { pattern: 'A', muted: false };
        });
        return { name, instruments };
    }

    /**
     * Retourne toutes les scenes
     */
    getScenes() {
        return this.scenes;
    }

    /**
     * Retourne une scene spécifique
     */
    getScene(sceneId) {
        return this.scenes[sceneId] || null;
    }

    /**
     * Retourne l'ID de la scene courante
     */
    getCurrentSceneId() {
        return this.currentSceneId;
    }

    /**
     * Crée une nouvelle scene
     * @param {string} name - Nom de la scene
     * @param {boolean} fromCurrent - Si true, copie l'état actuel
     */
    createScene(name, fromCurrent = false) {
        const id = 'scene_' + Date.now();

        if (fromCurrent && this.currentSceneId && this.scenes[this.currentSceneId]) {
            // Copie de la scene courante
            this.scenes[id] = JSON.parse(JSON.stringify(this.scenes[this.currentSceneId]));
            this.scenes[id].name = name;
        } else {
            // Nouvelle scene basée sur le pattern global actuel
            const instruments = {};
            this.instrumentIds.forEach(instId => {
                instruments[instId] = {
                    pattern: this.currentPatternId,
                    muted: false
                };
            });
            this.scenes[id] = { name, instruments };
        }

        this.notifySceneChange();
        this.save();
        return id;
    }

    /**
     * Met à jour une scene
     */
    updateScene(sceneId, data) {
        if (!this.scenes[sceneId]) return;
        Object.assign(this.scenes[sceneId], data);
        this.notifySceneChange();
        this.save();
    }

    /**
     * Met à jour le pattern d'un instrument dans une scene
     */
    setSceneInstrumentPattern(sceneId, instrumentId, pattern) {
        if (!this.scenes[sceneId]) return;
        if (!this.scenes[sceneId].instruments[instrumentId]) {
            this.scenes[sceneId].instruments[instrumentId] = { pattern: 'A', muted: false };
        }
        this.scenes[sceneId].instruments[instrumentId].pattern = pattern;

        // Notifier si c'est la scene courante en mode Scene ou Song
        if ((this.sceneMode || this.songMode) && sceneId === this.currentSceneId) {
            this.notifySceneChange();
        }
        this.save();
    }

    /**
     * Met à jour le mute d'un instrument dans une scene
     */
    setSceneInstrumentMuted(sceneId, instrumentId, muted) {
        if (!this.scenes[sceneId]) return;
        if (!this.scenes[sceneId].instruments[instrumentId]) {
            this.scenes[sceneId].instruments[instrumentId] = { pattern: 'A', muted: false };
        }
        this.scenes[sceneId].instruments[instrumentId].muted = muted;

        // Notifier si c'est la scene courante en mode Scene ou Song
        if ((this.sceneMode || this.songMode) && sceneId === this.currentSceneId) {
            this.notifySceneChange();
        }
        this.save();
    }

    /**
     * Supprime une scene
     */
    deleteScene(sceneId) {
        if (!this.scenes[sceneId]) return;

        // Ne pas supprimer la dernière scene
        if (Object.keys(this.scenes).length <= 1) return;

        delete this.scenes[sceneId];

        // Si c'était la scene courante, en sélectionner une autre
        if (this.currentSceneId === sceneId) {
            this.currentSceneId = Object.keys(this.scenes)[0];
        }

        this.notifySceneChange();
        this.save();
    }

    /**
     * Renomme une scene
     */
    renameScene(sceneId, newName) {
        if (!this.scenes[sceneId]) return;
        this.scenes[sceneId].name = newName;
        this.notifySceneChange();
        this.save();
    }

    /**
     * Duplique une scene
     */
    duplicateScene(sceneId) {
        if (!this.scenes[sceneId]) return null;

        const newId = 'scene_' + Date.now();
        const original = this.scenes[sceneId];
        this.scenes[newId] = {
            name: original.name + ' (copy)',
            instruments: JSON.parse(JSON.stringify(original.instruments))
        };

        this.notifySceneChange();
        this.save();
        return newId;
    }

    /**
     * Lance une scene (la rend active)
     */
    launchScene(sceneId) {
        if (!this.scenes[sceneId]) return;

        this.currentSceneId = sceneId;
        this.settings.currentSceneId = sceneId;
        this.notifySceneChange();
        this.save();
    }

    /**
     * Active/désactive le mode scene
     */
    setSceneMode(enabled) {
        this.sceneMode = enabled;
        this.settings.sceneMode = enabled;

        if (enabled && !this.currentSceneId) {
            // S'assurer qu'une scene est sélectionnée
            const sceneIds = Object.keys(this.scenes);
            if (sceneIds.length > 0) {
                this.currentSceneId = sceneIds[0];
                this.settings.currentSceneId = this.currentSceneId;
            }
        }

        this.notifySceneChange();
        this.save();
    }

    /**
     * Retourne si le mode scene est actif
     */
    isSceneMode() {
        return this.sceneMode;
    }

    /**
     * Retourne le pattern effectif pour un instrument
     * En mode Scene/Song: retourne le pattern assigné dans la scene courante
     * En mode Pattern: retourne le pattern global
     */
    getInstrumentPatternId(instrumentId) {
        // En mode Scene OU Song, on utilise les patterns par instrument
        const useScenePatterns = this.sceneMode || this.songMode;

        if (useScenePatterns && this.currentSceneId && this.scenes[this.currentSceneId]) {
            const scene = this.scenes[this.currentSceneId];
            if (scene.instruments && scene.instruments[instrumentId]) {
                return scene.instruments[instrumentId].pattern;
            }
        }
        return this.currentPatternId;
    }

    /**
     * Retourne les patterns effectifs pour un instrument
     * Cette méthode est utilisée par le Sequencer
     */
    getInstrumentPatterns(instrumentId) {
        const patternId = this.getInstrumentPatternId(instrumentId);
        return this.patternBanks[patternId];
    }

    /**
     * Vérifie si un instrument est muté dans la scene courante
     * Fonctionne en mode Scene ET en mode Song
     */
    isInstrumentMutedInScene(instrumentId) {
        // En mode Pattern, jamais de mute via scene
        if (!this.sceneMode && !this.songMode) {
            return false;
        }

        // Vérifier la scene courante
        if (!this.currentSceneId) {
            return false;
        }

        const scene = this.scenes[this.currentSceneId];
        if (!scene || !scene.instruments) {
            return false;
        }

        const instrument = scene.instruments[instrumentId];
        if (!instrument) {
            return false;
        }

        // Retourner explicitement true ou false
        return instrument.muted === true;
    }

    /**
     * Enregistre un callback pour les changements de scene
     */
    onSceneChange(callback) {
        this.sceneChangeListeners.push(callback);
        return () => {
            this.sceneChangeListeners = this.sceneChangeListeners.filter(l => l !== callback);
        };
    }

    notifySceneChange() {
        this.sceneChangeListeners.forEach(cb => cb({
            scenes: this.scenes,
            currentSceneId: this.currentSceneId,
            sceneMode: this.sceneMode
        }));
    }

    // ============================================
    // PATTERNS
    // ============================================

    setStep(patternName, step, value) {
        if (this.patterns[patternName]) {
            this.patterns[patternName][step] = value;
            this.notifyListeners();
            this.save();
        }
    }

    toggleStep(patternName, step) {
        if (this.patterns[patternName]) {
            this.patterns[patternName][step] = !this.patterns[patternName][step];
            this.notifyListeners();
            this.save();
            return this.patterns[patternName][step];
        }
        return false;
    }

    getStep(patternName, step) {
        return this.patterns[patternName]?.[step];
    }

    // ============================================
    // PARAMÈTRES SYNTH
    // ============================================

    /**
     * Obtient les paramètres d'un synthétiseur
     * @param {string} synthId - 'tb303_1', 'tb303_2', 'tr808'
     */
    getSynthParams(synthId) {
        return this.synthParams[synthId] || {};
    }

    /**
     * Obtient un paramètre spécifique d'un synthétiseur
     * @param {string} synthId - 'tb303_1', 'minimoog', etc.
     * @param {string} param - nom du paramètre
     * @returns {any} valeur du paramètre ou undefined
     */
    getSynthParam(synthId, param) {
        const params = this.synthParams[synthId];
        return params ? params[param] : undefined;
    }

    /**
     * Met à jour un paramètre d'un synthétiseur
     * @param {string} synthId - 'tb303_1', 'tb303_2', 'tr808'
     * @param {string} param - nom du paramètre
     * @param {any} value - nouvelle valeur
     */
    setSynthParam(synthId, param, value) {
        if (this.synthParams[synthId]) {
            this.synthParams[synthId][param] = value;
            this.save();
        }
    }

    /**
     * Met à jour tous les paramètres d'un synthétiseur
     */
    setSynthParams(synthId, params) {
        if (this.synthParams[synthId]) {
            Object.assign(this.synthParams[synthId], params);
            this.save();
        }
    }

    // ============================================
    // SETTINGS
    // ============================================

    getBpm() {
        return this.settings.bpm;
    }

    setBpm(value) {
        this.settings.bpm = value;
        this.save();
    }

    getSwing() {
        return this.settings.swing || 0;
    }

    setSwing(value) {
        this.settings.swing = value;
        this.save();
    }

    // ============================================
    // POWER STATES
    // ============================================

    /**
     * Obtient l'état power d'un instrument
     * @param {string} instrumentId - ID de l'instrument (ex: 'minimoog', 'tr808')
     * @returns {boolean} - true si allumé
     */
    getPowerState(instrumentId) {
        if (!this.settings.powerStates) {
            this.settings.powerStates = {};
        }
        return this.settings.powerStates[instrumentId] !== false; // true par défaut
    }

    /**
     * Définit l'état power d'un instrument
     * @param {string} instrumentId - ID de l'instrument
     * @param {boolean} isOn - true si allumé
     */
    setPowerState(instrumentId, isOn) {
        if (!this.settings.powerStates) {
            this.settings.powerStates = {};
        }
        this.settings.powerStates[instrumentId] = isOn;
        this.save();
    }

    // ============================================
    // MIXER STATE
    // ============================================

    getMixerState() {
        return this.mixerState;
    }

    setMixerState(state) {
        this.mixerState = state;
        this.save();
    }

    // ============================================
    // PANEL STATES (collapsed/expanded)
    // ============================================

    /**
     * Vérifie si un panel est collapsed
     * @param {string} panelId - ID du panel (ex: 'jupiter8', 'tr808')
     * @returns {boolean}
     */
    isPanelCollapsed(panelId) {
        return this.panelStates[panelId]?.collapsed === true;
    }

    /**
     * Définit l'état collapsed d'un panel
     * @param {string} panelId - ID du panel
     * @param {boolean} collapsed - true si collapsed
     */
    setPanelCollapsed(panelId, collapsed) {
        if (!this.panelStates[panelId]) {
            this.panelStates[panelId] = {};
        }
        this.panelStates[panelId].collapsed = collapsed;
        this.settings.panelStates = this.panelStates;
        this.save();
    }

    /**
     * Toggle l'état collapsed d'un panel
     * @param {string} panelId - ID du panel
     * @returns {boolean} - nouvel état
     */
    togglePanelCollapsed(panelId) {
        const newState = !this.isPanelCollapsed(panelId);
        this.setPanelCollapsed(panelId, newState);
        return newState;
    }

    // ============================================
    // GESTION GLOBALE
    // ============================================

    clearAll() {
        this.patternBanks = getDefaultPatternBanks();
        this.patterns = this.patternBanks[this.currentPatternId];
        this.synthParams = getDefaultSynthParams();
        // On garde le BPM actuel
        this.notifyListeners();
        this.save();
    }

    subscribe(callback) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    notifyListeners() {
        this.listeners.forEach(cb => cb(this.patterns));
    }

    save() {
        if (this.autoSave) {
            saveToLocalStorage(this.patternBanks, this.synthParams, this.settings, this.mixerState, this.song, this.scenes);
        }
    }

    // ============================================
    // EXPORT / IMPORT
    // ============================================

    exportToFile(filename) {
        exportToJSON(this.patternBanks, this.synthParams, this.settings, this.mixerState, this.scenes, this.song, filename);
    }

    async importFromFile(file) {
        try {
            const data = await importFromJSON(file);
            this.patternBanks = data.patternBanks;
            this.synthParams = data.synths;
            this.settings = data.settings;

            // Importer l'état du mixer si présent
            if (data.mixer) {
                this.mixerState = { ...getDefaultMixerState(), ...data.mixer };
            }

            // Importer les scenes si présentes
            if (data.scenes) {
                this.scenes = data.scenes;
            }

            // Importer le song si présent
            if (data.song) {
                this.song = data.song;
            }

            // Importer les panel states (collapsed) si présents
            if (this.settings.panelStates) {
                this.panelStates = this.settings.panelStates;
            }

            // Importer le mode scene si présent
            if (this.settings.sceneMode !== undefined) {
                this.sceneMode = this.settings.sceneMode;
            }
            if (this.settings.currentSceneId) {
                this.currentSceneId = this.settings.currentSceneId;
            }

            // Importer le mode song si présent
            if (this.settings.songMode !== undefined) {
                this.songMode = this.settings.songMode;
            }

            // Mettre à jour le pattern courant
            this.currentPatternId = this.settings.currentPattern || 'A';
            this.patterns = this.patternBanks[this.currentPatternId];

            this.notifyPatternChange(this.currentPatternId);
            this.notifySceneChange();
            this.notifySongChange();
            this.notifyListeners();
            this.save();
            return { success: true, settings: this.settings };
        } catch (e) {
            console.error('Erreur import:', e);
            return { success: false };
        }
    }

    /**
     * Charge un preset (patterns + optionnellement synths)
     * Le preset est chargé dans le pattern courant
     */
    loadPreset(preset) {
        if (!preset) return;

        // Charger les patterns dans le pattern courant
        Object.keys(preset).forEach(key => {
            if (this.patterns[key] !== undefined && Array.isArray(preset[key])) {
                this.patterns[key] = [...preset[key]];
            }
        });

        // S'assurer que la banque courante est mise à jour
        this.patternBanks[this.currentPatternId] = this.patterns;

        // Charger les paramètres synth si présents
        if (preset.synths) {
            Object.keys(preset.synths).forEach(synthId => {
                if (this.synthParams[synthId]) {
                    Object.assign(this.synthParams[synthId], preset.synths[synthId]);
                }
            });
        }

        this.notifyListeners();
        this.save();
    }
}
